import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, getStoredToken, getStoredUser, setStoredToken, setStoredUser } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(getStoredUser());
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const t = getStoredToken();
    if (!t) { setReady(true); return; }
    api.get("/auth/me")
      .then((r) => { setUser(r.data); setStoredUser(r.data); })
      .catch(() => { setStoredToken(null); setStoredUser(null); setUser(null); })
      .finally(() => setReady(true));
  }, []);

  const login = useCallback(async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    setStoredToken(r.data.token);
    setStoredUser(r.data.user);
    setUser(r.data.user);
    return r.data.user;
  }, []);

  const signup = useCallback(async (payload) => {
    const r = await api.post("/auth/signup", payload);
    setStoredToken(r.data.token);
    setStoredUser(r.data.user);
    setUser(r.data.user);
    return r.data.user;
  }, []);

  const logout = useCallback(() => {
    setStoredToken(null);
    setStoredUser(null);
    setUser(null);
    window.location.href = "/login";
  }, []);

  return (
    <AuthContext.Provider value={{ user, ready, login, signup, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}

export function roleLabel(role) {
  return ({
    super_admin: "Super Admin",
    gfmp_reviewer: "GFMP Reviewer",
    catalog_manager: "Catalog Manager",
    legal_review: "Legal Review",
    beta_tester: "Beta Tester",
    industry_beta_reviewer: "Industry Beta Reviewer",
    industry_access: "Industry Access (parked)",
  })[role] || role;
}

// MP10 — restricted external role for invited music supervisors / sync agents.
// This is the SINGLE source of truth for the role string in the frontend.
export const BETA_REVIEWER_ROLE = "industry_beta_reviewer";

export function isBetaReviewer(user) {
  return !!user && user.role === BETA_REVIEWER_ROLE;
}

export function canChangeClearance(user) {
  // Approving tracks to Internally / Client / Corporate-Ready is reserved for
  // super_admin and gfmp_reviewer. Legal Review may flag/hold but cannot approve.
  return !!user && (user.role === "super_admin" || user.role === "gfmp_reviewer");
}

export function canFlagAttorneyReview(user) {
  return !!user && ["super_admin", "gfmp_reviewer", "legal_review"].includes(user.role);
}

// Admin Walk-Through Correction — read-only role gating. Beta Tester and any
// role outside the writer set are blocked from CRUD writes on canonical entities.
export function canWrite(user) {
  return !!user && ["super_admin", "gfmp_reviewer", "catalog_manager"].includes(user.role);
}

// Assignment 2C — Brief Intelligence read and route gate. This mirrors the
// accepted backend Brief read-role set. Write actions remain gated separately
// through canWrite(user).
export function canAccessBriefIntelligence(user) {
  return !!user && [
    "super_admin",
    "gfmp_reviewer",
    "catalog_manager",
    "legal_review",
  ].includes(user.role);
}

export function isAdmin(user) {
  return !!user && user.role === "super_admin";
}

export function isSuperAdmin(user) {
  return isAdmin(user);
}
